'use client';

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Star } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useToggleFavorite } from '@/app/hooks/useToggleFavorite';
import { selectIsFavorited } from '@/app/store/slices/userAppAccessSlice';
import type { RootState } from '@/app/store';

interface TileStarButtonProps {
  sysAppId: number;
  className?: string;
  /**
   * - `overlay` (default): absolute-positioned over a tile's top-right corner.
   *   Used on grid tiles — mobile `/workspace` + `/main`, Favorites/Recents strips.
   * - `inline`: flow-positioned, for horizontal rows — desktop AppShell drawer.
   *
   * See `.kiro/specs/desktop-workspace-favorites/design.md` (D3).
   */
  variant?: 'overlay' | 'inline';
}

/**
 * Star-toggle button with optimistic visual and server-side persistence.
 * Flips immediately on tap; toasts an error and reverts if the server rejects.
 */
const TileStarButton: React.FC<TileStarButtonProps> = ({ sysAppId, className, variant = 'overlay' }) => {
  const { t } = useTranslation('workspace');
  const isFavorited = useSelector((state: RootState) => selectIsFavorited(state, sysAppId));
  const toggle = useToggleFavorite();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    toggle(sysAppId)
      .catch(() => {
        toast.error(t('favorites.toggleError'));
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isSubmitting}
      aria-label={t(isFavorited ? 'favorites.unfavorite' : 'favorites.favorite')}
      aria-pressed={isFavorited}
      className={cn(
        variant === 'overlay' && 'absolute top-1 right-1 z-10',
        'h-8 w-8 flex items-center justify-center rounded-full flex-shrink-0',
        'hover:bg-accent/60 active:bg-accent/80 transition-colors',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className
      )}
    >
      <Star
        className={cn(
          'w-4 h-4 transition-colors',
          isFavorited ? 'text-yellow-500' : 'text-muted-foreground'
        )}
        fill={isFavorited ? 'currentColor' : 'none'}
        strokeWidth={2}
      />
    </button>
  );
};

TileStarButton.displayName = 'TileStarButton';
export default React.memo(TileStarButton);
